import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useRecording } from "../../context/RecordingContext";
import {
  isRecordingProvider,
  isAppointmentToday,
  isNagDismissed,
  markNagDismissed,
} from "../../utils/recordingProviders";
import { isLiveTodayVisit } from "../../utils/visitGuard";

// Chart-level recording reminder:
//   • 30-second passive nag banner — fires once per appointmentid per
//     provider when they sit on a today's chart without starting recording
//     and there's no existing note for the visit.
//
// The leave-chart confirmation lives in patientchartwrap.jsx (it has to wrap
// the onClose handler). Stop-confirm threshold lives in StopConfirmModal.
// Recording-active state is signalled by the pulsing red dot on the sidebar
// mic in sideframe.jsx — the previous status chip above the timeline was
// removed in favor of that single, less cluttered indicator.
const NAG_THRESHOLD_SECONDS = 30;

const ChartRecordingNudge = ({ data, hasExistingNote, onStartRecording }) => {
  const userConfig = useSelector((state) => state?.userconfig);

  const { isGlobalRecording } = useRecording();

  const patientid = data?.patientid;
  const appointmentid = data?.appointmentid;
  // Pass the whole user object: isRecordingProvider derives "records encounters"
  // from providerid, so this works on any practice without an allowlist.
  // Only on the patient's live visit today (owner 10-09): any other chart has
  // no recording target until a visit is selected, so there is nothing to nag.
  const inScope =
    isRecordingProvider(userConfig) &&
    isAppointmentToday(data?.startdate || data?.encounterdate || data?.date) &&
    isLiveTodayVisit(data);

  const [secondsOnChart, setSecondsOnChart] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(() =>
    isNagDismissed(patientid, appointmentid)
  );

  // Reset elapsed counter + dismiss state when appointment changes
  useEffect(() => {
    setSecondsOnChart(0);
    setBannerDismissed(isNagDismissed(patientid, appointmentid));
  }, [patientid, appointmentid]);

  // Tick the on-chart timer only while the chart is mounted and the provider
  // hasn't started recording. Don't tick while recording (no need to nag).
  useEffect(() => {
    if (!inScope) return;
    if (isGlobalRecording) return;
    if (hasExistingNote) return;
    if (bannerDismissed) return;
    const t = setInterval(() => setSecondsOnChart((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [inScope, isGlobalRecording, hasExistingNote, bannerDismissed]);

  // Status chip removed — recording state is now signalled by the pulsing
  // red circle on the sidebar mic instead. The 30s nag banner remains as
  // the active reminder for unrecorded today's visits.
  const showBanner =
    inScope &&
    !isGlobalRecording &&
    !hasExistingNote &&
    !bannerDismissed &&
    secondsOnChart >= NAG_THRESHOLD_SECONDS;

  const handleDismissBanner = () => {
    markNagDismissed(patientid, appointmentid);
    setBannerDismissed(true);
  };

  const handleStartFromBanner = () => {
    if (typeof onStartRecording === "function") onStartRecording();
    handleDismissBanner();
  };

  if (!inScope) return null;

  return (
    <>
      {showBanner && (
        <div style={bannerStyles.container} role="status" aria-live="polite">
          <div style={bannerStyles.textRow}>
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>📝</span>
            <span style={{ flex: 1 }}>
              You haven't started recording this visit yet.
              {data?.firstname && data?.lastname
                ? ` (${data.firstname} ${data.lastname}, today)`
                : ""}
            </span>
          </div>
          <div style={bannerStyles.btnRow}>
            {typeof onStartRecording === "function" && (
              <button onClick={handleStartFromBanner} style={bannerStyles.primaryBtn}>
                Start recording
              </button>
            )}
            <button onClick={handleDismissBanner} style={bannerStyles.secondaryBtn}>
              Dismiss for this visit
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const bannerStyles = {
  container: {
    // Vertical stack: in the narrow CareIQ/side panel the old horizontal row
    // crushed the message to one word per line (QA 2026-08-20). Stacking keeps
    // it readable at any width.
    display: "flex",
    flexDirection: "column",
    gap: 10,
    margin: "8px 8px 0",
    padding: "10px 14px",
    background: "#FFF7ED",
    border: "1px solid #FB923C",
    borderRadius: 8,
    color: "#7C2D12",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  textRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  btnRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  primaryBtn: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #FB923C",
    background: "#FB923C",
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #FED7AA",
    background: "transparent",
    color: "#7C2D12",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
  },
};

export default ChartRecordingNudge;
